<template>
    <div class="container-fluid">
        <div class="row card-body profiles-list">
            <div class="col-12 d-flex justify-content-between">
                <div class="d-flex align-items-center">
                    <h4 class="text-uppercase mb-0">Top Profiles</h4>
                    <a href="/help/knowledgebase?section=rcmnd-prfls" target="_blank">
                        <img src="/img/icons/ic_info.svg" alt="About Recommended Profiles" class="ms-2" /></a>
                </div>
                <div class="d-flex form-inline gap-2">
                    <div class="form-group">
                        <PlatoonDropdown id="ddPlatoonFilter" :selected="selectedPlatoon" @selected-platoon-id="
                            (selectedValue) => (selectedPlatoon = selectedValue)
                        " />
                    </div>
                    <div class="form-group">
                        <SectionDropdown id="ddSectionFilter" :selected="selectedSection" @selected-section-id="
                            (selectedValue) => (selectedSection = selectedValue)
                        " />
                    </div>
                    <div class="form-group"></div>
                    <argon-input id="search" name="search" :value="searchText" type="text" placeholder="Search..."
                        aria-label="Search" class="ms-3" @input="(event) => (searchText = event.target.value)"
                        @keyup.enter="search()" />
                </div>
            </div>

            <div class="col-lg-9 col-12 card mt-3">
                <div class="table-responsive profiles-table card-body">
                    <el-table ref="dataTable" :data="candidates" style="width: 100%" @sort-change="sortColumn"
                        @row-click="handleTopCandidateRowClick">
                        <el-table-column prop="name" label="Name" width="400">
                            <template #default="scope">
                                <el-tooltip effect="customized" v-if="scope.row">
                                    <span>{{ scope.row.user[0].name }}</span>
                                </el-tooltip>
                            </template>
                        </el-table-column>
                        <el-table-column prop="gender" label="Gender" width="150">
                            <template #default="scope">
                                <span v-if="scope.row">{{ scope.row.user[0].gender }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="platoon_name" label="Platoon" width="250">
                            <template #default="scope">
                                <span v-if="scope.row">{{ scope.row.user[0].platoon_name }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="section_name" label="Section" width="150">
                            <template #default="scope">
                                <span v-if="scope.row">{{ scope.row.user[0].section_name }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="wellness_score" label="Wellness Score" width="175" sortable="custom">
                            <template #default="scope">
                                <argon-progress
                                    :color="getWellnessColor(scope.row.wellness_score)"
                                    :percentage="
                                        scope.row.wellness_score
                                        ? (scope.row.wellness_score * 100) / 100
                                        : 0"
                                    :left-text="`${scope.row.wellness_score ? scope.row.wellness_score : 0}`"
                                    :right-text="'100'"
                                />
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
                <div v-if="candidates.length > 0" class="card-footer">
                    <div class="d-flex justify-content-end align-items-center">
                        <app-pagination :records="count" :page-size="pageSize" :page="page" @update-page-selection="
                            (selectedValue) => updatePageSelection(selectedValue)
                        " />
                    </div>
                </div>
            </div>

            <div class="col-lg-3 col-12 mt-3">
                <user-profile-details v-if="selectedCandidate"
                    :dna-profile-id="selectedCandidate.dna_profile_id != null ? selectedCandidate.dna_profile_id : 'NA'"
                    :confidence="selectedCandidate.confidence" :user="selectedCandidate.user[0]"
                    :selected-candidate="selectedCandidate" :display-dna="false" />
            </div>
        </div>
    </div>
</template>

<script>
import Constants from "@/helpers/constants";
import ArgonInput from "@/shared/Controls/ArgonInput.vue";
import ArgonProgress from "@/shared/Controls/ArgonProgress.vue";
import PlatoonDropdown from "../components/PlatoonDropdown.vue";
import SectionDropdown from "../components/SectionDropdown.vue";
import UserProfileDetails from "./components/profiler/UserProfileDetails.vue";


export default {
    name: 'TopProfiles',
    components: {
        UserProfileDetails,
        ArgonInput,
        ArgonProgress,
        SectionDropdown,
        PlatoonDropdown
    },
    mounted() {
        this.fetchTopProfiles();
    },
    data() {
        return {
            topProfiles: [],
            selectedCandidate: null,
            selectedSection: -1, 
            searchText: "",
            selectedPlatoon: -1,
            count: 0,
            page: 1,
            pageSize: Constants.DEFAULT_PAGE_SIZE,
            minConfidence: 0,
            maxConfidence: 100,
            profileTypes: [],
            candidates: [],
            queryProfileType: null,
            sort: 'wellness_score',
            order: 'desc',
            selectedProfileTypeId: 'a3452f94738cbeefe8c79c79f7336522',
        };
    },
    methods: {
        fetchTopProfiles() {
            this.$store
                .dispatch("data/candidatesForProfileType", {
                    dna_profile_id: this.selectedProfileTypeId,
                    sort: this.sort,
                    order: this.order,
                    page: this.page,
                    page_size: this.pageSize,
                    min_confidence: this.minConfidence || 0,
                    max_confidence: this.maxConfidence || 100,
                    search_term: this.searchText || "",
                    platoon_id: this.selectedPlatoon || -1,
                    section_id: this.selectedSection || -1,
                })
                .then(({ data }) => {
                    const { count, rows } = data;
                    this.count = count;
                    if (rows && count > 0) {
                        this.candidates = rows;
                        this.selectedCandidate = rows[0];
                    } else {
                        this.candidates = [];
                        this.selectedCandidate = null;
                    }
                });
        },
        handleTopCandidateRowClick(candidate) {
            this.selectedCandidate = candidate;
        },
        sortColumn({ prop, order }) {
            this.sort = prop;
            this.order = order == "ascending" ? "asc" : "desc";
            this.fetchTopProfiles();
        },
        updatePageSelection(newPageIndex) {
            this.page = newPageIndex;
            this.fetchTopProfiles();
        },
        search() {
            this.page = 1;
            this.fetchTopProfiles();
        },
        getWellnessColor(wellness_score) {
            let color = "";
            if(wellness_score >= 0 && wellness_score < 50) {
                color = "#ffffff";
            } else if (wellness_score >= 50 && wellness_score < 70) {
                color = "#e7e79f";
            } else {
                color = "#fdff18";      
            }

            return color;
        },
    },
    watch: {
        selectedPlatoon() {
            this.page = 1;
            this.fetchTopProfiles();
        },
        selectedSection() {
            this.page = 1;
            this.fetchTopProfiles();
        },
        searchText() {
            if (this.searchText.trim().length == 0) this.fetchTopProfiles();
        },
    },
}
</script>


<style lang="scss" scoped>
.profiles-list {
    border-radius: 0.5rem;
}

.profiles-table {
    background: #283335;
    border-radius: 0.5rem;
}

.table-responsive {
    margin-top: 1rem;
}
</style>